import { Stack } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { INVOICE_TYPES, WCC_STATUSES_ARRAY } from '@sr/dto';
import { DateUrlParamsFilter } from '../../entities/filters/date-url-params-filter';
import { SimpleParamFilter } from '../../shared/ui/param-filters/simple-param-filter';
import { useOurCompany } from '../../hooks/portal-settings.hook';
import { LegalEntityParamFilter } from '../../entities/legal-entities/legal-entity-param-filter';
import { ManagerUrlParamsFilter } from 'entities/filters/manager-url-params-filter';
import { FilterWrapper, ValueFormatterContext } from 'shared/ui/filter/filter-wrapper';
import { useManagersDropdownList } from 'features/user/user-picker/ui';
import { useLegalEntityLabel } from 'shared/hooks/use-entity-labels';

interface WccFilterProps {
  showTypeFilter?: boolean;
}

export const WccFilter = ({ showTypeFilter = false }: WccFilterProps) => {
  const ourCompanyId = useOurCompany();
  const { items: managers } = useManagersDropdownList();
  const [searchParams] = useSearchParams();
  const selectedLegalEntityId = searchParams.get('ourLegalEntityId');
  const legalEntityLabel = useLegalEntityLabel(selectedLegalEntityId);

  const managerValueFormatter = ({ value }: ValueFormatterContext) => {
    const manager = managers.find((item) => String(item.id) === value);
    return manager ? `${manager.firstName} ${manager.secondName || ''}`.trim() : value;
  };

  const createOptionFormatter = (options: { id: number | string; label: string }[]) => {
    const map = new Map(options.map((item) => [String(item.id), item.label]));
    return ({ value }: ValueFormatterContext) => map.get(String(value)) ?? value;
  };

  const statusFormatter = createOptionFormatter(WCC_STATUSES_ARRAY);
  const typeFormatter = createOptionFormatter(INVOICE_TYPES);

  const legalEntityValueFormatter = ({ value }: ValueFormatterContext) => {
    if (legalEntityLabel && selectedLegalEntityId === value) {
      return legalEntityLabel;
    }
    return value;
  };

  return (
    <FilterWrapper title="Фильтры актов">
      <FilterWrapper.Item label="Наше юрлицо" queryKeys="ourLegalEntityId" valueFormatter={legalEntityValueFormatter}>
        <LegalEntityParamFilter queryParamName={'ourLegalEntityId'} label="Наше юрлицо" companyId={ourCompanyId} />
      </FilterWrapper.Item>

      <FilterWrapper.Item label="Менеджер" queryKeys="managerId" valueFormatter={managerValueFormatter}>
        <ManagerUrlParamsFilter queryParamName="managerId" />
      </FilterWrapper.Item>
      {showTypeFilter && (
        <FilterWrapper.Item label="Тип" queryKeys="type" valueFormatter={typeFormatter}>
          <SimpleParamFilter queryParamName="type" items={INVOICE_TYPES} label="Тип" />
        </FilterWrapper.Item>
      )}
      <FilterWrapper.Item label="Статус" queryKeys="status" valueFormatter={statusFormatter}>
        <SimpleParamFilter<string> queryParamName="status" items={WCC_STATUSES_ARRAY} label="Статус" />
      </FilterWrapper.Item>

      <FilterWrapper.Item
        label="Дата окончания"
        queryKeys={['fromDate', 'toDate']}
        summaryFormatter={({ searchParams }) => {
          const from = searchParams.get('fromDate');
          const to = searchParams.get('toDate');
          if (!from && !to) return 'Дата окончания';
          if (from && to) return `Окончание: ${from} — ${to}`;
          return `Окончание: ${from || to}`;
        }}
      >
        <Stack spacing={1.5}>
          <DateUrlParamsFilter queryParamName={'fromDate'} label="Дата окончания с " />
          <DateUrlParamsFilter queryParamName={'toDate'} label="Дата окончания по" />
        </Stack>
      </FilterWrapper.Item>
    </FilterWrapper>
  );
};
